
import { useMemo } from 'react';
import { contentLinker } from '@/services/contentLinker';
import { sanitizeHtml } from '@/utils/sanitizeHtml';

/**
 * Normalize heading levels so they don't skip (e.g. h1→h3 becomes h2→h3).
 * Since blog posts already have an h1 (the title), content headings start at h2.
 */
function normalizeHeadingLevels(html: string): string {
  // Find all heading levels used in the content
  const usedLevels = new Set<number>();
  html.replace(/<h([1-6])\b/gi, (_, level) => {
    usedLevels.add(parseInt(level, 10));
    return '';
  });

  if (usedLevels.size === 0) return html;

  // Build a mapping from used levels to sequential levels starting at h2
  const sorted = Array.from(usedLevels).sort((a, b) => a - b);
  const levelMap: Record<number, number> = {};
  sorted.forEach((level, i) => {
    levelMap[level] = Math.min(i + 2, 6); // start at h2
  });

  // Replace opening and closing heading tags
  return html.replace(/<(\/?)h([1-6])(\s|>)/gi, (match, slash, level, rest) => {
    const mapped = levelMap[parseInt(level, 10)];
    return mapped !== undefined ? `<${slash}h${mapped}${rest}` : match;
  });
}

interface AutoLinkedContentProps {
  content: string;
  className?: string;
}

const AutoLinkedContent = ({ content, className }: AutoLinkedContentProps) => {
  const processedContent = useMemo(() => {
    const linkedContent = contentLinker.processContent(content);
    const sanitized = sanitizeHtml(linkedContent, {
      allowLinks: true,
      allowBasicFormatting: true,
      allowImages: true
    });
    return normalizeHeadingLevels(sanitized);
  }, [content]);

  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: processedContent }} 
    />
  );
};

export default AutoLinkedContent;
